import {forwardRef, useState} from 'react';
import type {CSSProperties, InputHTMLAttributes, ReactNode} from 'react';

const EYE_SLOT = 44;

const EyeIcon = ({off}: {off: boolean}) => (
    <svg
        aria-hidden="true"
        fill="none"
        height="18"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        viewBox="0 0 24 24"
        width="18"
    >
        <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
        <circle cx="12" cy="12" r="3" />
        {off && <path d="m3 3 18 18" />}
    </svg>
);

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
    // Styling stays with the caller so each page keeps its own theme.
    style?: CSSProperties;
    // Reserve room for anything already in the field, e.g. a "Forgot?" link.
    rightInset?: number;
    iconColor?: string;
    trailing?: ReactNode;
};

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
    function PasswordInput(
        {rightInset = 0, iconColor = '#7C8B9A', trailing, style, ...inputProps},
        ref
    ) {
        const [visible, setVisible] = useState(false);

        return (
            <div style={{position: 'relative'}}>
                <input
                    ref={ref}
                    {...inputProps}
                    style={{...style, paddingRight: EYE_SLOT + rightInset}}
                    type={visible ? 'text' : 'password'}
                />
                <button
                    aria-label={visible ? 'Hide password' : 'Show password'}
                    aria-pressed={visible}
                    style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        right: rightInset,
                        width: EYE_SLOT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: iconColor
                    }}
                    type="button"
                    onClick={() => setVisible(v => !v)}
                >
                    <EyeIcon off={visible} />
                </button>
                {trailing}
            </div>
        );
    }
);
